import { useRef, useState } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';

import {
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Dialog as UiDialog,
} from '@/components/ui/dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Semen } from '@/rtk-query.gql';
import { SemenDto } from '@/services/semen-service/types';

import { NewSemenTransactionForm } from '../new-semen-transaction-form';
import { NewSemenTransactionFormValues } from '../new-semen-transaction-form/schema';

type AddTransactionDialogProps = {
  gridRef?: React.RefObject<AgGridReact>;
  visible: boolean;
  onHide: () => void;
  semenId: string;
  semen?: SemenDto | null;
  semens?: SemenDto[];
  onSuccess?: () => void;
};

export const AddTransactionDialog = ({
  gridRef,
  visible,
  onHide,
  semenId,
  semen,
  semens,
  onSuccess,
}: AddTransactionDialogProps) => {
  const { t } = useTranslation();
  const dialogRef = useRef<Dialog>(null);
  const [dialogContainer, setDialogContainer] = useState<HTMLElement>();
  const [baseDialogContainer, setBaseDialogContainer] =
    useState<HTMLElement | null>(null);
  const isBaseUi = useFeatureFlag('add_transaction_prime_to_base_ui');
  const handleSuccess = (data: NewSemenTransactionFormValues) => {
    gridRef?.current?.api?.refreshInfiniteCache();
    onSuccess?.();
  };

  if (isBaseUi) {
    return (
      <UiDialog
        open={visible}
        onOpenChange={(open) => {
          if (!open) onHide();
        }}
      >
        <DialogContent
          preflight={false}
          ref={setBaseDialogContainer}
          className="z-[2000] md:max-w-[1000px]"
          overlayClassName="z-[2000]"
        >
          <DialogHeader className="preflight">
            <DialogTitle>
              {t('semenTransaction.newSemenTransaction')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <NewSemenTransactionForm
              semenId={semenId}
              semen={semen as Semen | undefined}
              semens={semens}
              onSuccess={handleSuccess}
              onCancel={onHide}
              calendarContainer={baseDialogContainer ?? undefined}
            />
          </DialogBody>
        </DialogContent>
      </UiDialog>
    );
  }

  return (
    <Dialog
      ref={dialogRef}
      header={t('semenTransaction.newSemenTransaction')}
      visible={visible}
      onHide={onHide}
      modal
      className="w-[1000px] max-w-[95vw]"
      onShow={() =>
        setDialogContainer(dialogRef.current?.getMask() ?? undefined)
      }
    >
      <NewSemenTransactionForm
        semenId={semenId}
        semen={semen as Semen | undefined}
        semens={semens}
        onSuccess={handleSuccess}
        onCancel={onHide}
        calendarContainer={dialogContainer}
      />
    </Dialog>
  );
};
